<!-- eslint-disable vue/no-deprecated-dollar-listeners-api -->
<template>
  <span :class="$attrs.class" :style="$attrs.style">
    <template v-if="styledRadiobox">
      <span :class="[radioBtnCssClass, checkedCssClass, { disabled: disabled }]" data-test="s-radio-button-pseudo-label">
        <span v-if="isButtonType"
              aria-hidden="true"
              data-test="s-radio-button-pseudo-label-text-sr"
        >{{ labelText }}</span>
      </span>
    </template>

    <template v-else>
      <div
        v-if="isScreenReaderActive"
        data-test="s-radio-button-sr-wrapper"
        :class="[
          labelCssClass,
          labelCheckedCssClass,
          additionalContentCssClass,
          additionalContentCheckedCssClass,
          { 'pointer-none': disabled },
        ]"
      >
        <label
          :for="`radio-${inputId}-${valueOnSelection}`"
          data-test="s-radio-button-label"
          class="mb-0"
          :class="{ disabled: disabled, 'sr-only': !isButtonType }"
          :tabindex="isAndroid || isIOS ? -1 : undefined"
          aria-hidden="true"
        >
          {{ labelText }}
        </label>

        <template v-if="hasAdditionalContent">
          <!-- We wrap the slot-content in an aria-hidden span to make sure that only the prop "labelText" is read by screenreaders. -->
          <span aria-hidden="true" class="additional-content-wrapper w-100 d-flex justify-content-between align-items-center flex-column">
            <slot name="additional-content"></slot>
          </span>
        </template>

        <!-- NOTE: This slot is only needed in rare use-cases, when SR is active and the RadioButton has a aria-hidden label -->
        <slot name="additional-sr-content"></slot>

        <input
          v-bind="filteredAttrs"
          :id="`radio-${inputId}-${valueOnSelection}`"
          :disabled="disabled"
          type="radio"
          :name="name"
          :value="valueOnSelection"
          :checked="isChecked ? 'checked' : undefined"
          :aria-label="labelText"
          class="hide-input"
          :class="[inputCssClass, inputCheckedCssClass]"
          data-test="s-radio-button-input"
          :aria-hidden="disabled && hasAdditionalSRContent && isIOS ? 'true' : undefined"
          :tabindex="isAndroid || isIOS ? 0 : undefined"
          @input="(val) => (radioButtonValue = val)"
          v-on="_listeners"
          @focusin="focusClassIsActive = true"
          @focusout="focusClassIsActive = false"
        />
      </div>

      <label
        v-else
        class="mb-0"
        data-test="s-radio-button-label"
        :class="[{ disabled: disabled }, labelCssClass, labelCheckedCssClass, additionalContentCssClass, additionalContentCheckedCssClass]"
        :tabindex="isAndroid ? -1 : undefined"
      >
        <template v-if="hasAdditionalContent">
          <!-- We wrap the slot-content in an aria-hidden span to make sure that only the prop "labelText" is read by screenreaders. -->
          <span aria-hidden="true" class="w-100 d-flex justify-content-between align-items-center flex-column">
            <slot name="additional-content"></slot>
          </span>
        </template>

        <input
          v-bind="filteredAttrs"
          :id="`radio-${valueOnSelection}`"
          v-model="radioButtonValue"
          :disabled="disabled"
          type="radio"
          :name="name"
          :value="valueOnSelection"
          :checked="isChecked ? 'checked' : undefined"
          :aria-label="labelText"
          class="hide-input"
          :class="[inputCssClass, inputCheckedCssClass]"
          data-test="s-radio-button-input"
          :tabindex="isAndroid || isIOS ? 0 : undefined"
          v-on="_listeners"
          @focusin="focusClassIsActive = true"
          @focusout="focusClassIsActive = false"
        />
        <span :class="{ 'sr-only': !isButtonType }"
              class="text-break-hyphens"
              aria-hidden="true"
              data-test="s-radio-button-label-text-sr"
        >{{ labelText }}</span>
      </label>
    </template>
  </span>
</template>

<script>
export default {
  name: 'SRadioButton',
  props: {
    currentlySelectedValue: {
      type: [String, Number, Object, Boolean],
      default: null,
    },

    valueOnSelection: {
      type: [String, Number, Object, Boolean],
      required: true,
    },

    radioButtonType: {
      type: String,
      default: 'checkmark',
      validator: function (value) {
        return ['checkmark', 'round', 'color-picker', 'btn'].includes(value)
      },
    },

    labelText: {
      type: String,
      default: '',
      required: true,
    },

    name: {
      type: String,
      required: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    // Enforce the styled checkbox (no input + label) for cases when the comp is being used for example on SduiUserListItem
    forceStyledCheckbox: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    inputId: null,
    focusClassIsActive: false,
    isAndroid: false,
    isIOS: false,
    isScreenReaderActive: false,
  }),

  computed: {
    filteredAttrs() {
      const { class: _cls, style: _style, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    radioButtonValue: {
      get: function () {
        return this.currentlySelectedValue
      },

      set: function () {
        // Communicate the change to parent component so that the selected value can be updated
        this.$emit('change', this.valueOnSelection)
      },
    },

    radioBtnCssClass() {
      const classes = [`${this.radioButtonType}-radio-button`]
      if (this.radioButtonType === 'color-picker') classes.push(`${this.valueOnSelection}-bg`)
      return classes
    },

    checkedCssClass() {
      if (!this.isChecked) return ''
      return this.radioBtnCssClass.map((c) => `${c}--checked`)
    },

    inputCssClass() {
      if (this.isButtonType) return 'btn-radio-button-input'
      return this.radioBtnCssClass
    },

    inputCheckedCssClass() {
      if (this.isButtonType || !this.isChecked) return ''
      return this.checkedCssClass
    },

    // If the radio button is a button, we need to apply the styles to the label, instead of the input
    labelCssClass() {
      if (!this.isButtonType) return ''
      return this.radioBtnCssClass
    },

    labelCheckedCssClass() {
      if (!this.isButtonType || !this.isChecked) return ''
      return this.checkedCssClass
    },

    additionalContentCssClass() {
      if (!this.hasAdditionalContent) return
      if (this.focusClassIsActive) return 'additional-content additional-content--focus'
      return 'additional-content'
    },

    additionalContentCheckedCssClass() {
      if (!this.isChecked || !this.hasAdditionalContent) return ''
      if (this.hasAdditionalContent) return 'additional-content--checked'
      return ''
    },

    isChecked() {
      return this.currentlySelectedValue === this.valueOnSelection
    },

    _listeners() {
      // we need to remove the change listener, otherwise we get side effects
      // eslint-disable-next-line
      const { change, ...listeners } = this.$listeners;
      return listeners
    },

    styledRadiobox() {
      // If the parent (e.g. SduiListItem) has a @click-listener, it will most likely turn into a <b-button>.
      // We must not render the input + label element inside of it in this case to avoid invalid HTML which can't be
      // accessed by screen readers.

      // TODO VUE3: If the parent element is a bootstrap component, the call to "this.$parent?.$listeners" will
      // throw an error "INSTANCE_LISTENERS compat has been disabled". That is because $listeners do not exist in Vue 3.
      // Change this to $attrs.onClick when the compat mode is removed.
      try {
        return !!this.$parent?.$listeners?.click || this.forceStyledCheckbox
      } catch (e) {
        // Catch bootstrap error
        if (e?.message?.includes('INSTANCE_LISTENERS compat has been disabled')) return false
        throw e
      }
    },

    hasAdditionalContent() {
      return this.$hasSlotContent(this.$slots['additional-content'])
    },

    hasAdditionalSRContent() {
      return this.$hasSlotContent(this.$slots['additional-sr-content'])
    },

    isButtonType() {
      return this.radioButtonType === 'btn'
    },
  },

  emits: ['change'],

  watch: {
    '$store.state.isScreenReaderActive'(newVal) {
      this.isScreenReaderActive = newVal
    },
  },

  beforeMount() {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
  },

  async mounted() {
    if (import.meta.env.IS_STORYBOOK) return
    this.isIOS = this.$store.state.isIOS
    this.isAndroid = this.$store.state.isAndroid
  },

  created() {
    this.inputId = this.$uuid()
  },
}
</script>

<style scoped lang="scss">
// SHARED STYLES -------------------------------------------------------------
.color-picker-radio-button,
.checkmark-radio-button,
.round-radio-button {
  position: relative;
  cursor: pointer;
  display: block;
  margin: 0;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  border-radius: 50%;
  background-color: $color-bg-primary;
}

.checkmark-radio-button,
.round-radio-button {
  border: 1px solid $color-gray-300;
}

.color-picker-radio-button::after,
.checkmark-radio-button::after,
.round-radio-button::after {
  content: '';
  position: absolute;
}

// CHECKMARK RADIO BUTTON -------------------------------------------------------------
.checkmark-radio-button::after {
  top: 8px;
  left: 7px;
  width: 12px;
  height: 6px;
  border: 2px solid $color-white;
  border-top: none;
  border-right: none;
  opacity: 0;
  transform: rotate(-45deg);
}

.checkmark-radio-button--checked {
  background-color: $color-primary;
  border-color: $color-primary;
}

.checkmark-radio-button--checked::after {
  opacity: 1;
}

// ROUND RADIO BUTTON -------------------------------------------------------------
.round-radio-button--checked {
  border: 2px solid $color-primary !important;
}

.round-radio-button--checked::after {
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  background-color: $color-primary;
  border-radius: 50%;
}

// COLOR-PICKER RADIO BUTTON -------------------------------------------------------------
.color-picker-radio-button {
  width: 40px;
  height: 40px;
}

.color-picker-radio-button::after {
  top: 15px;
  left: 12px;
  width: 16px;
  height: 6px;
  border: 2px solid $color-white;
  border-top: none;
  border-right: none;
  opacity: 0;
  transform: rotate(-45deg);
}

.color-picker-radio-button--checked::after {
  opacity: 1;
}

// BTN RADIO BUTTON -------------------------------------------------------------
.btn-radio-button-input {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 24px;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.btn-radio-button {
  border-radius: 24px;
  color: $color-gray-300;
  border: $color-gray-300 1px solid;
  padding: 6px 12px;
  font-size: pxtorem(15);
  font-weight: $font-weight-normal;
  min-height: initial;
  display: inline-block;
  margin: 0;
  cursor: pointer;
  position: relative;
  max-width: 100%;

  &:hover {
    background-color: $color-primary-light;
    color: $color-primary;
    border-color: $color-primary;
  }
}

.btn-radio-button--checked,
.btn-radio-button--checked:hover {
  color: $color-white;
  background-color: $color-primary;
  border-color: $color-primary;
}

// ADDITIONAL CONTENT -------------------------------------------------------------
.additional-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid $color-gray-100;
  border-radius: 12px;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  height: 100%;
  justify-content: space-between;
  font-size: pxtorem(16);
}

// CUSTOM HANDLING FOR FOCUS AND ADDITIONAL-CONTENT ---------------------------------------------
// remove focus frame from input
.keyboard-navigation .additional-content input:focus-visible {
  outline: none !important;
  outline-offset: unset;
}

// ... and put it on to the label instead
.keyboard-navigation .additional-content--focus {
  outline: 3px solid $color-primary !important;
  outline-offset: 2px;
  z-index: 1;
}

.additional-content--checked {
  border-color: $color-primary;
  opacity: 1;
}

// disabled state (possible button + input)
input[type='radio']:disabled,
.checkmark-radio-button.disabled,
.round-radio-button.disabled,
.color-picker-radio-button.disabled,
.additional-content.disabled,
.btn-radio-button.disabled {
  user-select: none;
  pointer-events: none;
  opacity: 0.6;
  filter: grayscale(1);
}

// HIDE INPUT STYLES SO WE CAN USE OUR OWN -------------------------------------------------------------
.hide-input {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;

  &:focus {
    -webkit-box-shadow: none;
    box-shadow: none;
  }
}
</style>
